import {
  canManageMember,
  canManageRole,
  compareRolePositions,
  computeEffectiveChannelPermissions,
  computeGuildPermissions,
  DM_PERMISSIONS,
  explainChannelPermission,
  hasPermission,
  isTimedOut,
  OverwriteType,
  PermissionFlags,
  type PermissionDecision,
  type PermissionGuild,
  type PermissionMember,
  type PermissionOverwrite,
  type PermissionRole,
  THREAD_CHANNEL_TYPES,
} from '../contracts/chat-permissions.js';
import type {
  APIChannel,
  APIMessage,
  APIRole,
} from '../contracts/chat-types.js';
import { MessageType } from '../contracts/chat-types.js';
import type { ChatState, Snowflake } from '../stores/state.js';

const F = PermissionFlags;

/**
 * Permissões no cliente, com o mesmo cálculo da API (o do contrato). Servem
 * só para esconder e desabilitar o que a pessoa não pode; quem decide é
 * sempre a API.
 */

export function permissionGuild(
  state: ChatState,
  guildId: Snowflake,
): PermissionGuild | null {
  const guild = state.guilds[guildId];
  if (!guild) return null;
  return {
    id: guild.id,
    ownerId: guild.owner_id,
    roles: new Map(
      Object.values(guild.roles).map((role) => [role.id, permissionRole(role)]),
    ),
  };
}

export function permissionRole(role: APIRole): PermissionRole {
  return {
    id: role.id,
    permissions: BigInt(role.permissions),
    position: role.position,
  };
}

export function permissionMember(
  state: ChatState,
  guildId: Snowflake,
  userId: Snowflake,
): PermissionMember | null {
  const member = state.members[guildId]?.[userId];
  if (!member) return null;
  return {
    userId,
    roleIds: member.roles,
    communicationDisabledUntil: member.communication_disabled_until,
    pending: member.pending,
  };
}

export function overwritesOf(
  channel: APIChannel | undefined,
): PermissionOverwrite[] {
  return (channel?.permission_overwrites ?? []).map((overwrite) => ({
    id: overwrite.id,
    type:
      overwrite.type === OverwriteType.MEMBER
        ? OverwriteType.MEMBER
        : OverwriteType.ROLE,
    allow: BigInt(overwrite.allow),
    deny: BigInt(overwrite.deny),
  }));
}

/** Permissões efetivas de alguém (padrão: eu) num canal; 0 se não dá para saber. */
export function channelPermissions(
  state: ChatState,
  channelId: Snowflake,
  userId: Snowflake | undefined = state.me?.id,
  now: Date = new Date(),
): bigint {
  const channel = state.channels[channelId];
  if (!channel || !userId) return 0n;
  if (!channel.guild_id) return DM_PERMISSIONS;
  const guild = permissionGuild(state, channel.guild_id);
  const member = permissionMember(state, channel.guild_id, userId);
  if (!guild || !member) return 0n;
  const isThread = THREAD_CHANNEL_TYPES.has(channel.type);
  const source = isThread
    ? channel.parent_id
      ? state.channels[channel.parent_id]
      : undefined
    : channel;
  return computeEffectiveChannelPermissions({
    guild,
    member,
    channelType: channel.type,
    overwrites: overwritesOf(source),
    joined: userId === state.me?.id && !!state.joinedThreads[channelId],
    now,
  });
}

/** Permissões no servidor (sem canal): gerenciar cargos, banir, castigar… */
export function guildPermissions(
  state: ChatState,
  guildId: Snowflake,
  userId: Snowflake | undefined = state.me?.id,
  now: Date = new Date(),
): bigint {
  if (!userId) return 0n;
  const guild = permissionGuild(state, guildId);
  const member = permissionMember(state, guildId, userId);
  if (!guild || !member) return 0n;
  return computeGuildPermissions(guild, member, now);
}

export function can(
  state: ChatState,
  channelId: Snowflake,
  flag: bigint,
): boolean {
  return hasPermission(channelPermissions(state, channelId), flag);
}

export function canInGuild(
  state: ChatState,
  guildId: Snowflake,
  flag: bigint,
): boolean {
  return hasPermission(guildPermissions(state, guildId), flag);
}

/** Posso gerenciar (apelido, cargos, castigo, expulsar, banir) esta pessoa? */
export function canManageUser(
  state: ChatState,
  guildId: Snowflake,
  targetId: Snowflake,
): boolean {
  const me = state.me?.id;
  const guild = permissionGuild(state, guildId);
  const actor = me ? permissionMember(state, guildId, me) : null;
  const target = permissionMember(state, guildId, targetId);
  return (
    !!guild && !!actor && !!target && canManageMember(guild, actor, target)
  );
}

/** Posso editar, dar ou tirar este cargo (abaixo do meu mais alto)? */
export function canManageRoleId(
  state: ChatState,
  guildId: Snowflake,
  roleId: Snowflake,
): boolean {
  const me = state.me?.id;
  const guild = permissionGuild(state, guildId);
  const actor = me ? permissionMember(state, guildId, me) : null;
  const role = state.guilds[guildId]?.roles[roleId];
  return (
    !!guild &&
    !!actor &&
    !!role &&
    canManageRole(guild, actor, permissionRole(role))
  );
}

/** Cargos do servidor do mais alto para o mais baixo (o @everyone por último). */
export function sortedRoles(state: ChatState, guildId: Snowflake): APIRole[] {
  const roles = Object.values(state.guilds[guildId]?.roles ?? {});
  return roles.sort((a, b) =>
    compareRolePositions(permissionRole(b), permissionRole(a)),
  );
}

/** "Verificar acesso": por que alguém pode (ou não) algo num canal. */
export function explainPermission(
  state: ChatState,
  channelId: Snowflake,
  userId: Snowflake,
  flag: bigint,
  now: Date = new Date(),
): PermissionDecision | null {
  const channel = state.channels[channelId];
  if (!channel?.guild_id) return null;
  const guild = permissionGuild(state, channel.guild_id);
  const member = permissionMember(state, channel.guild_id, userId);
  if (!guild || !member) return null;
  const isThread = THREAD_CHANNEL_TYPES.has(channel.type);
  const source =
    isThread && channel.parent_id ? state.channels[channel.parent_id] : channel;
  return explainChannelPermission(
    guild,
    member,
    { type: channel.type, overwrites: overwritesOf(source) },
    flag,
    { now, isThread },
  );
}

export type ComposerState =
  | { canSend: true; slowmodeSeconds: number }
  | {
      canSend: false;
      /** Por que não: para o texto do compositor. */
      reason:
        | 'no-permission'
        | 'timeout'
        | 'pending'
        | 'locked-thread'
        | 'not-writable';
    };

const WRITABLE_TYPES = new Set([0, 1, 3, 5, 10, 11, 12]);

/** O compositor mostra o campo ou o motivo de não poder escrever (como o Discord). */
export function composerState(
  state: ChatState,
  channelId: Snowflake,
  now: Date = new Date(),
): ComposerState {
  const channel = state.channels[channelId];
  if (!channel || !WRITABLE_TYPES.has(channel.type)) {
    return { canSend: false, reason: 'not-writable' };
  }
  const permissions = channelPermissions(state, channelId, state.me?.id, now);
  const isThread = THREAD_CHANNEL_TYPES.has(channel.type);
  if (channel.guild_id && state.me) {
    const member = state.members[channel.guild_id]?.[state.me.id];
    const restricted = permissionMember(state, channel.guild_id, state.me.id);
    if (member?.pending && !hasPermission(permissions, F.ADMINISTRATOR)) {
      return { canSend: false, reason: 'pending' };
    }
    if (
      restricted &&
      isTimedOut(restricted, now) &&
      !hasPermission(permissions, F.ADMINISTRATOR)
    ) {
      return { canSend: false, reason: 'timeout' };
    }
  }
  if (
    isThread &&
    channel.thread_metadata?.locked &&
    channel.thread_metadata.archived &&
    !hasPermission(permissions, F.MANAGE_THREADS)
  ) {
    return { canSend: false, reason: 'locked-thread' };
  }
  const send = isThread ? F.SEND_MESSAGES_IN_THREADS : F.SEND_MESSAGES;
  if (!hasPermission(permissions, send)) {
    return { canSend: false, reason: 'no-permission' };
  }
  const exempt =
    hasPermission(permissions, F.MANAGE_MESSAGES) ||
    hasPermission(permissions, F.MANAGE_CHANNELS) ||
    hasPermission(permissions, F.BYPASS_SLOWMODE);
  return {
    canSend: true,
    slowmodeSeconds: exempt ? 0 : (channel.rate_limit_per_user ?? 0),
  };
}

/** Tipos de mensagem que a pessoa edita (as comuns e as respostas). */
const EDITABLE_TYPES = new Set<number>([
  MessageType.DEFAULT,
  MessageType.REPLY,
]);
/** Mensagens do sistema que não se fixa nem se responde. */
const SYSTEM_TYPES = new Set<number>([
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 15, 18, 21, 22, 24, 46,
]);

export type MessageAbilities = {
  edit: boolean;
  delete: boolean;
  pin: boolean;
  react: boolean;
  reply: boolean;
  createThread: boolean;
  /** Esconder/mostrar a prévia de links da mensagem de outra pessoa. */
  suppressEmbeds: boolean;
};

/** O que o menu da mensagem oferece (a API confere de novo). */
export function messageAbilities(
  state: ChatState,
  message: APIMessage,
  now: Date = new Date(),
): MessageAbilities {
  const channel = state.channels[message.channel_id];
  const permissions = channelPermissions(
    state,
    message.channel_id,
    state.me?.id,
    now,
  );
  const own = message.author.id === state.me?.id && !message.webhook_id;
  const system = SYSTEM_TYPES.has(message.type);
  const isThread = !!channel && THREAD_CHANNEL_TYPES.has(channel.type);
  const composer = composerState(state, message.channel_id, now);
  return {
    // Enquete não se edita (como no Discord).
    edit: own && EDITABLE_TYPES.has(message.type) && !message.poll,
    delete:
      own ||
      (!!channel?.guild_id && hasPermission(permissions, F.MANAGE_MESSAGES)),
    pin:
      !system &&
      (hasPermission(permissions, F.PIN_MESSAGES) ||
        hasPermission(permissions, F.MANAGE_MESSAGES)),
    react:
      hasPermission(permissions, F.READ_MESSAGE_HISTORY) &&
      (hasPermission(permissions, F.ADD_REACTIONS) ||
        !!message.reactions?.length),
    reply:
      !system &&
      composer.canSend &&
      hasPermission(permissions, F.READ_MESSAGE_HISTORY),
    createThread:
      !system &&
      !isThread &&
      !!channel?.guild_id &&
      !message.thread &&
      hasPermission(permissions, F.CREATE_PUBLIC_THREADS),
    suppressEmbeds: own || hasPermission(permissions, F.MANAGE_MESSAGES),
  };
}
